import { Component, EventEmitter, Input, Output } from '@angular/core';
import { PsychologicalSurveyApiService } from '@app/core/service/api/psychologicalSurvey.api.service';
import { ToastService } from '@app/core/service/toast.service';
import { UserEntityService } from '@app/core/service/user-entity.service';
import { MongoStoredObject } from '@app/types/mongo-stored-object';
import { isMobile } from '@app/utils/is-mobile';
import { IPsychologicalSurvey } from '@backend/models/types/psychological-survey';
import { IPsychologicalSurveyAnswerValue } from '@backend/models/types/psychological-survey-answer';
import {
  EPsychologicalSurveyQuestionType,
  IPsychologicalSurveyQuestion
} from '@backend/models/types/psychological-survey-question';
import { LoadingController } from '@ionic/angular';

@Component({
  selector: 'app-survey-preview',
  templateUrl: './survey-preview.component.html',
  styleUrls: ['./survey-preview.component.scss'],
  // eslint-disable-next-line @angular-eslint/prefer-standalone
  standalone: false
})
export class SurveyPreviewComponent {
  @Input()
  public readonly survey: MongoStoredObject<IPsychologicalSurvey> = null;

  @Input()
  public readonly questions: MongoStoredObject<IPsychologicalSurveyQuestion>[] =
    [];

  @Output()
  public cancelClicked = new EventEmitter<void>();

  @Output()
  public surveyCompleted = new EventEmitter<void>();

  @Output()
  public surveySkipped = new EventEmitter<void>();

  protected psychologicalSurveyQuestionType = EPsychologicalSurveyQuestionType;

  protected currentQuestionIndex = 0;
  protected answers: {
    question: string;
    result: IPsychologicalSurveyAnswerValue;
  }[] = [];
  protected isM = false;

  public constructor(
    private readonly _psychologicalSurveyApiService: PsychologicalSurveyApiService,
    private readonly _toastSerivce: ToastService,
    private readonly _loadingCtrl: LoadingController,
    private readonly _userEntityService: UserEntityService
  ) {
    this.isM = isMobile();
  }

  protected get currentQuestion() {
    if (this.questions.length === 0) {
      return null;
    }

    const question = this.questions[this.currentQuestionIndex];
    const answer = question
      ? this.answers.find((a) => a.question === question._id)?.result
      : null;

    return {
      question,
      answer
    } as any;
  }

  protected async onQuestionComplete(event: {
    questionId: string;
    result: IPsychologicalSurveyAnswerValue;
  }) {
    this.answers.push({
      question: event.questionId,
      result: event.result
    });

    if (this.currentQuestionIndex === this.questions.length - 1) {
      const loading = await this._loadingCtrl.create({
        message: 'Loading...'
      });
      loading.present();

      this._psychologicalSurveyApiService
        .answerToPsychologicalSurvey({
          path: { surveyId: this.survey._id },
          body: { questionAnswers: this.answers }
        })
        .subscribe({
          next: () => {
            loading.dismiss();
            this._userEntityService.updateUserFromBackend();
            this.surveyCompleted.emit();
            this.onCancelClicked();
          },
          error: () => {
            loading.dismiss();
            this._toastSerivce.presentToast('Unable to complete the survey');
          }
        });
    } else {
      this.currentQuestionIndex++;
    }
  }

  protected onStepBack() {
    if (this.currentQuestionIndex === 0) {
      this.onCancelClicked();
      return;
    }

    this.currentQuestionIndex--;
  }

  protected onCancelClicked() {
    this.cancelClicked.emit();
  }

  protected async onSkipClicked() {
    const loading = await this._loadingCtrl.create({
      message: 'Skipping...'
    });
    loading.present();

    this._psychologicalSurveyApiService
      .skipPsychologicalSurvey({
        path: { surveyId: this.survey._id }
      })
      .subscribe({
        next: () => {
          loading.dismiss();
          this.surveySkipped.emit();
          this.onCancelClicked();
        },
        error: () => {
          loading.dismiss();
          this._toastSerivce.presentToast('Unable to skip the survey');
        }
      });
  }
}
